import React from "react";
import { Zap, Sliders } from "lucide-react";
import GlowArea from "./charts/GlowArea";

/**
 * DCA Strategy
 *
 * Source of truth: `dcaData` prop (lives in FinancialDashboard, autosaves to Firestore).
 * No internal useState/useEffect — every user action pushes to parent immediately,
 * which prevents the previous bug where mounting the component would clobber saved
 * manual entries with auto-computed values.
 */

/* Tab signature accent — the NEON teal family from the chart tone system. Sourced
   from the same CSS var GlowArea's `cyan` tone resolves, so the chart, its
   highlights, and the stat badge glow with ONE colour and re-theme together. */
const TEAL = "var(--chart-cyan)";
const labelCls =
  "text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4";

const DCATab = ({ netIncomeData, dcaData, setDcaData }) => {
  const autoDcaEnabled = dcaData.autoDcaEnabled ?? true;
  const autoPercent = dcaData.autoPercent ?? 20;

  // Derive manual history — fall back to seeded zeros if nothing saved yet.
  const manualHistory =
    dcaData.history && dcaData.history.length
      ? dcaData.history
      : netIncomeData.map((r) => ({ month: r.month, dcaContribution: 0 }));

  // Compute auto values inline — never overwrite saved manual history.
  const autoHistory = netIncomeData.map((r) => ({
    month: r.month,
    dcaContribution: Math.max(0, (r.netIncome || 0) * (autoPercent / 100)),
  }));

  const displayHistory = autoDcaEnabled ? autoHistory : manualHistory;

  const totalNet = netIncomeData.reduce((s, r) => s + (r.netIncome || 0), 0);
  const avgNet = totalNet / (netIncomeData.length || 1);
  const targetMonthly = avgNet * (autoPercent / 100);

  /* ─── number formatting helpers ──────────────────────────────────────── */
  const fmtCurrency = (n) =>
    `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  /* ─── handlers — every change pushes to parent ───────────────────────── */
  const toggleAuto = () => {
    const next = !autoDcaEnabled;
    setDcaData({
      ...dcaData,
      autoDcaEnabled: next,
      autoPercent,
      history: next ? autoHistory : manualHistory,
      monthlyAmount: next ? targetMonthly : 0,
    });
  };

  const updatePercent = (value) => {
    const pct = Math.max(0, Math.min(100, parseFloat(value) || 0));
    const newAuto = netIncomeData.map((r) => ({
      month: r.month,
      dcaContribution: Math.max(0, (r.netIncome || 0) * (pct / 100)),
    }));
    setDcaData({
      ...dcaData,
      autoPercent: pct,
      ...(autoDcaEnabled && {
        history: newAuto,
        monthlyAmount: avgNet * (pct / 100),
      }),
    });
  };

  const updateManualDca = (month, value) => {
    const updated = manualHistory.map((entry) =>
      entry.month === month
        ? { ...entry, dcaContribution: parseFloat(value) || 0 }
        : entry
    );
    setDcaData({
      ...dcaData,
      autoDcaEnabled: false, // editing implicitly switches to manual
      history: updated,
      monthlyAmount: 0,
    });
  };

  /* ─── render ─────────────────────────────────────────────────────────── */
  return (
    <div className="bg-arc-card rounded-arc-card shadow-arc-sm ring-1 ring-arc-line p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <p className={`${labelCls} mb-2`}>Strategy</p>
        <h2 className="text-2xl font-bold tracking-tight text-arc-ink mb-2">
          Dollar-Cost Averaging
        </h2>
        <p className="text-arc-ink-3 text-sm leading-relaxed max-w-2xl">
          Automatically allocate a percentage of your monthly profit to DCA, or
          manually set each month. Your changes save automatically.
        </p>
      </div>

      {/* Mode toggle + percent input */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 rounded-arc-card ring-1 ring-arc-line bg-arc-paper-2">
        <button
          onClick={toggleAuto}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-arc-ctl text-sm font-semibold transition-all ${
            autoDcaEnabled
              ? "bg-arc-accent text-white shadow-arc-sm"
              : "bg-arc-card text-arc-ink-3 ring-1 ring-arc-line hover:text-arc-ink"
          }`}
        >
          <Zap size={15} strokeWidth={autoDcaEnabled ? 2.5 : 1.75} />
          Auto
        </button>
        <button
          onClick={toggleAuto}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-arc-ctl text-sm font-semibold transition-all ${
            !autoDcaEnabled
              ? "bg-arc-accent text-white shadow-arc-sm"
              : "bg-arc-card text-arc-ink-3 ring-1 ring-arc-line hover:text-arc-ink"
          }`}
        >
          <Sliders size={15} strokeWidth={!autoDcaEnabled ? 2.5 : 1.75} />
          Manual
        </button>

        <div className="flex items-center gap-2 ml-auto">
          <span className={labelCls}>Auto %</span>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={autoPercent}
            onChange={(e) => updatePercent(e.target.value)}
            className="w-20 px-3 py-2 rounded-arc-ctl text-sm tnum text-right bg-arc-card text-arc-ink ring-1 ring-arc-line focus:ring-arc-accent focus:outline-none disabled:opacity-50"
            disabled={!autoDcaEnabled}
          />
          <span className="text-arc-ink-3 text-sm">of monthly profit</span>
        </div>
      </div>

      {/* Per-month table — always visible. Editable in manual mode. */}
      <div className="overflow-x-auto mb-6 rounded-arc-card ring-1 ring-arc-line">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className={`text-left px-4 py-3 border-b border-arc-line-2 ${labelCls}`}>
                Month
              </th>
              <th className={`text-right px-4 py-3 border-b border-arc-line-2 ${labelCls}`}>
                Net Income
              </th>
              <th className={`text-right px-4 py-3 border-b border-arc-line-2 ${labelCls}`}>
                Auto DCA
              </th>
              <th className={`text-right px-4 py-3 border-b border-arc-line-2 ${labelCls}`}>
                Manual DCA
              </th>
            </tr>
          </thead>
          <tbody>
            {netIncomeData.map((r) => {
              const auto = Math.max(0, (r.netIncome || 0) * (autoPercent / 100));
              const manualValue =
                manualHistory.find((m) => m.month === r.month)
                  ?.dcaContribution || 0;
              return (
                <tr key={r.month} className="hover:bg-arc-paper">
                  <td className="px-4 py-2.5 font-medium text-arc-ink border-b border-arc-line">
                    {r.month}
                  </td>
                  <td className="px-4 py-2.5 text-right tnum text-arc-ink-2 border-b border-arc-line">
                    {fmtCurrency(r.netIncome)}
                  </td>
                  <td
                    className="px-4 py-2.5 text-right tnum border-b border-arc-line"
                    style={
                      autoDcaEnabled
                        ? { color: TEAL, fontWeight: 600 }
                        : undefined
                    }
                  >
                    <span className={autoDcaEnabled ? "" : "text-arc-ink-4"}>
                      {fmtCurrency(auto)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right border-b border-arc-line">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={manualValue}
                      onChange={(e) =>
                        updateManualDca(r.month, e.target.value)
                      }
                      className={`w-28 px-2 py-1 rounded-arc-sm text-sm tnum text-right bg-arc-card ring-1 ring-arc-line focus:ring-arc-accent focus:outline-none ${
                        !autoDcaEnabled ? "text-arc-ink" : "text-arc-ink-4"
                      }`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Chart — Auto Projection / Manual Plan as a gains-grade GlowArea in the
          tab accent (teal): smooth monotone curve, gradient stroke + area fill,
          shared arcGlow bloom. */}
      <div className="rounded-arc-card ring-1 ring-arc-line bg-arc-paper-2 p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className={labelCls}>
            {autoDcaEnabled ? "Auto Projection" : "Manual Plan"}
          </span>
          <span className="text-xs tnum text-arc-ink-3">
            {displayHistory.length} months
          </span>
        </div>
        <GlowArea
          data={displayHistory}
          xKey="month"
          series={[{ key: "dcaContribution", name: "DCA", tone: "cyan" }]}
          height={250}
          yTickFormatter={(v) =>
            `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`
          }
          valueFormatter={(value) => fmtCurrency(value)}
        />
      </div>

      {/* Summary footer */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-arc-card ring-1 ring-arc-line bg-arc-paper-2">
          <p className={`${labelCls} mb-1`}>Avg Net Income</p>
          <p className="text-xl tnum text-arc-ink">{fmtCurrency(avgNet)}</p>
        </div>
        <div className="p-4 rounded-arc-card ring-1 ring-arc-line bg-arc-paper-2">
          <p className={`${labelCls} mb-1`}>Target DCA / mo</p>
          <p
            className="text-xl tnum font-semibold"
            style={{ color: TEAL, textShadow: `0 0 14px ${TEAL}` }}
          >
            {fmtCurrency(targetMonthly)}
          </p>
        </div>
        <div className="p-4 rounded-arc-card ring-1 ring-arc-line bg-arc-paper-2">
          <p className={`${labelCls} mb-1`}>Mode</p>
          <p className="text-xl tnum text-arc-ink">
            {autoDcaEnabled ? `Auto · ${autoPercent}%` : "Manual"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DCATab;
